/*
Timeline view of the releases calendar: studios as rows — only those with a release of the
chosen type, the freshest first (on text the major labs first) — and months as columns: the
whole year the radio keeps and one month ahead. The table scrolls sideways inside itself (the
studio column stays); it opens on the chosen month with the next one at its right edge, and
the past is a scroll to the left. A cell holds the studio's models of the chosen type released
that month. On the text filter the studio column also shows the expected next release (one
estimate from the release rhythm and rumours) and its chip sits in the month of the range's
middle day: "sep 30 – oct 2" is an October estimate. Every row is the same height: as many
chip places as the fullest cell of the chosen type needs, from MIN_CHIP_PLACES to
CHIPS_PER_CELL; models come before the expected chip (it is in the studio column too), and a
cell with more models than places shows one fewer and "+N more" in the last place.
On a phone the studio column is the logo alone with the start of the expected range under it
("~oct 6"), and the table opens with the chosen month at its right edge, the month before it
in view.
*/
import { element } from './eventsData';
import { middleDay, monthKey, monthLabel, shiftMonth, shortDate } from './releasesData';
import { chancePercent, chanceText, columnStudioMark, dateRange, expectedChip, modelChip } from './releasesView';

// The radio keeps a year of releases; one month ahead is enough for the expected ones.
const MONTHS_BACK = 12;
const MONTHS_AHEAD = 1;
// Four places cover 97% of text cells (checked on the year's data, 30.09); more is an outlier.
const CHIPS_PER_CELL = 4;
// Fewer than three places do not hold a studio name on two lines: the row would grow again.
const MIN_CHIP_PLACES = 3;

/**
 * @param {{studios: Array, releases: Array}} data calendar from the radio
 * @param {{type: string, month: string}} state chosen type and month (YYYY-MM)
 * @param {Date} now
 * @returns {HTMLElement}
 */
export function buildTimeline(data, state, now) {
    const months = timelineMonths(data, now);
    const context = { months, ...cellContext(state, now) };
    const releases = data.releases.filter((release) => release.type === state.type);
    const grid = element('div', 'th-rel-timeline');
    grid.setAttribute('role', 'table');
    grid.style.setProperty('--months', String(months.length));
    const rows = studiosByFreshness(data.studios, releases, state.type).map(({ studio, own }) => ({
        studio,
        cells: months.map((key) => monthParts({ studio, releases: own, key }, context)),
    }));
    const slots = chipPlaces(rows);
    grid.style.setProperty('--slots', String(slots));
    grid.append(headerRow(context));
    rows.forEach((row) => grid.append(studioRow(row, { ...context, slots })));
    return grid;
}

/** Chip places in every cell: the fullest cell of the view, from MIN_CHIP_PLACES to
 * CHIPS_PER_CELL. */
function chipPlaces(rows) {
    const fullest = rows.reduce(
        (most, row) => row.cells.reduce((inRow, parts) => Math.max(inRow, chipCount(parts)), most),
        MIN_CHIP_PLACES,
    );
    return Math.min(fullest, CHIPS_PER_CELL);
}

/**
 * The timeline's months, oldest first: from the month of the oldest release the radio sent
 * (at most MONTHS_BACK back) to MONTHS_AHEAD after the current one. The same for every type,
 * so switching the type keeps the columns.
 * @param {{releases: Array}} data calendar from the radio
 * @param {Date} now
 * @returns {string[]} YYYY-MM keys
 */
export function timelineMonths(data, now) {
    const current = monthKey(now);
    const oldest = data.releases.reduce((first, release) => {
        const key = monthKey(release.date);
        return key < first ? key : first;
    }, current);
    const limit = shiftMonth(current, -MONTHS_BACK);
    const first = oldest < limit ? limit : oldest;
    const months = [first];
    while (months[months.length - 1] < shiftMonth(current, MONTHS_AHEAD)) {
        months.push(shiftMonth(months[months.length - 1], 1));
    }
    return months;
}

/**
 * Scrolls the drawn timeline so the chosen month shows whole, beside the pinned studio
 * column, with the month after it at the right edge when both fit (on a phone only the
 * chosen one does). Call once the table is on the page.
 * @param {HTMLElement} grid the table from buildTimeline
 * @param {string} month chosen YYYY-MM
 */
export function scrollToMonth(grid, month) {
    const heads = [...grid.querySelectorAll('.th-rel-timeline__month')];
    const index = heads.findIndex((head) => head.dataset.month === month);
    if (index < 0) {
        return;
    }
    const chosen = heads[index].getBoundingClientRect();
    const edge = heads[Math.min(index + 1, heads.length - 1)].getBoundingClientRect();
    const pinned = grid.querySelector('.th-rel-timeline__studio').getBoundingClientRect();
    const view = grid.getBoundingClientRect();
    const withNext = edge.right - (view.left + grid.clientLeft + grid.clientWidth);
    const chosenWhole = chosen.left - pinned.right;
    grid.scrollLeft = Math.max(0, grid.scrollLeft + Math.min(withNext, chosenWhole));
}

/**
 * Scrolls the drawn timeline so the chosen month ends at its right edge — on a phone, where
 * one and a half months fit, the month before it stays in view. Call once the table is on
 * the page.
 * @param {HTMLElement} grid the table from buildTimeline
 * @param {string} month chosen YYYY-MM
 */
export function scrollToMonthEnd(grid, month) {
    const head = grid.querySelector(`.th-rel-timeline__month[data-month="${month}"]`);
    if (!head) {
        return;
    }
    const view = grid.getBoundingClientRect();
    const viewRight = view.left + grid.clientLeft + grid.clientWidth;
    grid.scrollLeft = Math.max(0, grid.scrollLeft + head.getBoundingClientRect().right - viewRight);
}

/**
 * Studios with a release of the chosen type (the radio keeps the last year), freshest
 * release first. On text the major labs (tier 1) go before the rest; in image, video, speech
 * and music the big labs are not the main makers, so there freshness alone decides — a lab
 * silent for a year does not open the table with an empty row. A studio with none of this
 * type in the year has no row.
 */
function studiosByFreshness(studios, releases, type) {
    const byTier = type === 'text';
    return studios
        .map((studio) => ({ studio, own: releases.filter((item) => item.studio === studio.id) }))
        .filter(({ own }) => own.length > 0)
        .map((entry) => ({ ...entry, latest: latestDate(entry.own) }))
        .sort((first, second) => (byTier ? first.studio.tier - second.studio.tier : 0)
            || second.latest.localeCompare(first.latest));
}

function latestDate(releases) {
    return releases.reduce((last, release) => (release.date > last ? release.date : last), '');
}

function headerRow(context) {
    const row = element('div', 'th-rel-timeline__row th-rel-timeline__row--head');
    row.setAttribute('role', 'row');
    row.append(cell('th-rel-timeline__studio', 'Studio', 'columnheader'));
    context.months.forEach((key) => {
        const head = cell('th-rel-timeline__month', monthLabel(key, false), 'columnheader');
        head.dataset.month = key;
        if (key === context.current) {
            head.dataset.current = 'true';
            head.append(element('span', 'th-rel-timeline__now', 'Now'));
        }
        row.append(head);
    });
    return row;
}

function cell(className, text, role) {
    const node = element('div', className, text);
    node.setAttribute('role', role);
    return node;
}

/**
 * @param {{studio: Object, cells: Array<{models: HTMLElement[], expected: ?HTMLElement}>}}
 *     studioCells the studio and the chips of each month (monthParts)
 */
function studioRow({ studio, cells }, context) {
    const row = element('div', 'th-rel-timeline__row');
    row.setAttribute('role', 'row');
    row.append(studioCell(studio, context));
    context.months.forEach((key, index) => {
        const month = cell('th-rel-timeline__cell', undefined, 'cell');
        month.dataset.current = String(key === context.current);
        month.append(...limited(cells[index], `more:${studio.id}:${key}`, context.slots));
        row.append(month);
    });
    return row;
}

function studioCell(studio, context) {
    const head = cell('th-rel-timeline__studio', undefined, 'rowheader');
    const name = element('p', 'th-rel-timeline__name');
    name.append(columnStudioMark(studio), element('span', 'th-rel-timeline__studio-name', studio.name));
    head.append(name);
    if (context.withExpected && studio.expected) {
        head.append(soonLine(studio.expected), expectedLine(studio.expected));
    }
    return head;
}

/** Phone studio column: "Next" over "~Oct 4", the start of the expected range. */
function soonLine(expected) {
    const line = element('p', 'th-rel-timeline__soon');
    line.append(
        element('span', 'th-rel-timeline__soon-label', 'Next'),
        element('span', 'th-rel-timeline__soon-date', `~${shortDate(expected.start)}`),
    );
    return line;
}

function expectedLine(expected) {
    const line = element('p', 'th-rel-timeline__expected');
    line.append(element('span', 'th-rel-timeline__label', 'Next release'));
    const value = element('span', 'th-rel-timeline__when', dateRange(expected));
    if (expected.p30 !== null) {
        value.append(element('span', 'th-rel-timeline__chance', ` · ${chancePercent(expected.p30)}%`));
    }
    line.append(value);
    line.title = chanceText(expected);
    return line;
}

/**
 * Every chip of one timeline cell — for the "+N more" window, so it lists exactly what the
 * cell holds, the expected release included.
 * @param {{studios: Array, releases: Array}} data calendar from the radio
 * @param {{state: Object, now: Date}} view chosen type and today
 * @param {{studio: string, key: string}} cell studio id and month (YYYY-MM)
 * @returns {HTMLElement[]}
 */
export function cellChips(data, view, cell) {
    const studio = data.studios.find((item) => item.id === cell.studio);
    if (!studio) {
        return [];
    }
    const releases = data.releases.filter(
        (release) => release.type === view.state.type && release.studio === cell.studio,
    );
    return monthChips({ studio, releases, key: cell.key }, cellContext(view.state, view.now));
}

function cellContext(state, now) {
    return { now, current: monthKey(now), withExpected: state.type === 'text' };
}

/** Every chip of the cell, the expected release last. */
function monthChips(cell, context) {
    const { models, expected } = monthParts(cell, context);
    return expected ? [...models, expected] : models;
}

/**
 * @param {{studio: Object, releases: Array, key: string}} cell the studio's models of the type
 * @returns {{models: HTMLElement[], expected: ?HTMLElement}} model chips and the expected release
 */
function monthParts(cell, context) {
    const models = cell.releases.filter((release) => monthKey(release.date) === cell.key)
        .map((release) => modelChip(release, cell.studio));
    const forecast = context.withExpected ? cell.studio.expected : null;
    const placedHere = forecast && monthKey(middleDay(forecast)) === cell.key;
    const expected = placedHere ? expectedChip(cell.studio) : null;
    return { models, expected };
}

function chipCount({ models, expected }) {
    return models.length + (expected ? 1 : 0);
}

/**
 * What the cell shows in its places. Models come first: the expected release takes a place only
 * if one is free — it is also in the studio column. More models than places: one place fewer of
 * them and "+N more" in the last one, its window listing the whole cell with the forecast.
 */
function limited(parts, moreKey, places) {
    const { models, expected } = parts;
    if (chipCount(parts) <= places) {
        return expected ? [...models, expected] : models;
    }
    if (models.length <= places) {
        return models;
    }
    const shown = places - 1;
    const more = element('button', 'th-rel-more', `+${chipCount(parts) - shown} more`);
    more.type = 'button';
    more.dataset.pop = moreKey;
    return [...models.slice(0, shown), more];
}
